/**
 * Cart import functionality
 * @module shared-cart/import
 */

import { UserMessages } from '../constants.js';
import { base64UrlDecode } from '../utils/base64.js';
import { stripGid, extractSkus } from '../utils/gid.js';
import { getHandoffParam, removeUrlParam } from '../utils/url.js';
import { displayError } from '../ui/error.js';
import { clearCart, addToCart, applyDiscountCode, refreshCartUI } from '../cart/api.js';
import { showCartSpinner } from '../cart/sections.js';
import { getVariantsBySku, getInventories, checkItemAvailability } from '../external/inventory.js';
import type { InventoryItem, Variant } from '../external/inventory.js';

export interface HandoffItem {
  id?: string | number | null;
  qty?: string | number;
  gwp?: boolean | string;
}

interface PromotionCode {
  code: string;
}

interface HandoffPayload {
  items: HandoffItem[];
  promotionCodes?: PromotionCode[];
}

export interface PreparedItem {
  sku: string;
  id: number;
  quantity: number;
  gwp?: boolean;
}

interface PreparedItems {
  available: PreparedItem[];
  unavailable: PreparedItem[];
  toUpdate: PreparedItem[];
}

/**
 * @typedef {Object} HandoffPayload
 * @property {HandoffItem[]} items - Cart items to import
 * @property {PromotionCode[]} [promotionCodes] - Discount codes to apply
 */

/**
 * @typedef {Object} HandoffItem
 * @property {string} id - SKU of the product
 * @property {number} qty - Quantity to add
 * @property {boolean} [gwp] - Whether this is a gift with purchase item
 */

/**
 * @typedef {Object} PromotionCode
 * @property {string} code - The discount code string
 */

/**
 * @typedef {Object} PreparedItem
 * @property {string} sku - Product SKU
 * @property {number} id - Variant ID
 * @property {number} quantity - Quantity to add
 * @property {boolean} [gwp] - Whether this is a gift with purchase item
 */

/**
 * Parses the base64-encoded handoff payload from the URL
 * @returns {HandoffPayload|null} The parsed payload or null if invalid
 */
export function parseHandoffPayload(): HandoffPayload | null {
  const handoff = getHandoffParam();
  if (!handoff) return null;

  try {
    const jsonStr = base64UrlDecode(handoff);
    const payload = JSON.parse(jsonStr) as HandoffPayload;

    if (!payload?.items?.length) {
      console.warn('[Shared Cart] Payload has no items');
      return null;
    }

    console.info('[Shared Cart] Parsed handoff payload:', payload);
    return payload;
  } catch (error) {
    console.error('[Shared Cart] Failed to parse handoff payload:', error);
    return null;
  }
}

/**
 * Validates items against inventory and prepares them for cart addition
 * @param {HandoffItem[]} items - Raw items from handoff
 * @param {Array<{id: string, sku: string}>} variants - Resolved variants from lookup
 * @param {Array<{stockKeepingUnit: string, isAvailable: boolean}>} inventories - Current inventory levels
 * @param {number[]} currentVariantIds - Variant IDs already in cart
 * @returns {{available: PreparedItem[], unavailable: PreparedItem[], toUpdate: PreparedItem[]}}
 */
export function validateAndPrepareItems(
  items: HandoffItem[],
  variants: Variant[],
  inventories: InventoryItem[],
  currentVariantIds: number[],
): PreparedItems {
  const available: PreparedItem[] = [];
  const unavailable: PreparedItem[] = [];
  const toUpdate: PreparedItem[] = [];

  for (const rawItem of items) {
    const sku = String(rawItem.id);

    // Find matching variant
    const variant = variants.find((v) => v.sku.toString() === sku);
    if (!variant) {
      console.warn(`[Shared Cart] Variant not found for SKU: ${sku}`);
      continue;
    }

    // Extract numeric variant ID
    const variantId = variant?.id ? Number(stripGid(variant.id)) : null;
    if (!variantId) {
      console.warn(`[Shared Cart] Could not extract variant ID for SKU: ${sku}`);
      continue;
    }

    const alreadyInCart = currentVariantIds.includes(variantId);

    const quantity = rawItem?.qty ? Number(rawItem.qty) : 1;
    const isGwp = rawItem?.gwp === true || rawItem?.gwp === 'true';

    if (isGwp && quantity > 1) {
      // Split: 1 unit as GWP, remainder as regular purchase
      const gwpItem = { sku, id: variantId, quantity: 1, gwp: true };
      const regularItem = { sku, id: variantId, quantity: quantity - 1 };

      const isAvailable = checkItemAvailability({ sku, id: variantId, quantity }, inventories);
      if (isAvailable) {
        // GWP always goes to available — _gwp property makes it a distinct Shopify line item
        available.push(gwpItem);
        if (alreadyInCart) {
          toUpdate.push(regularItem);
        } else {
          available.push(regularItem);
        }
      } else {
        unavailable.push({ sku, id: variantId, quantity, gwp: true });
        console.warn(`[Shared Cart] SKU ${sku} is out of stock`);
      }
    } else {
      const preparedItem = {
        sku,
        id: variantId,
        quantity,
        ...(isGwp && { gwp: true }),
      };

      // Check inventory
      const isAvailable = checkItemAvailability(preparedItem, inventories);

      if (isAvailable) {
        if (isGwp) {
          // GWP always goes to available — _gwp property makes it a distinct Shopify line item
          available.push(preparedItem);
        } else if (alreadyInCart) {
          toUpdate.push(preparedItem);
        } else {
          available.push(preparedItem);
        }
      } else {
        unavailable.push(preparedItem);
        console.warn(`[Shared Cart] SKU ${sku} is out of stock`);
      }
    }
  }

  return { available, unavailable, toUpdate };
}

/**
 * Main cart import orchestrator - imports items from handoff payload
 * @returns {Promise<void>}
 */
export async function importCart(): Promise<void> {
  // Step 1: Parse handoff payload
  const payload = parseHandoffPayload();
  if (!payload) return;

  const skus = extractSkus(payload.items);
  if (!skus.length) {
    console.warn('[Shared Cart] No valid SKUs in payload');
    return;
  }

  console.info('[Shared Cart] Starting cart import:', {
    skuCount: skus.length,
  });

  showCartSpinner();

  // Step 2: Clear existing cart for a clean import
  try {
    await clearCart();
    console.info('[Shared Cart] Cart cleared');
  } catch (error) {
    console.error('[Shared Cart] Error clearing cart:', error);
  }

  // Step 3: Import products (skip intermediate renders)
  try {
    const [variants, inventories] = await Promise.all([
      getVariantsBySku(skus),
      getInventories(skus),
    ]);

    const { available, unavailable } = validateAndPrepareItems(
      payload.items,
      variants,
      inventories,
      [],
    );

    // Notify user of partial import
    if (unavailable.length > 0) {
      displayError(UserMessages.PARTIAL_IMPORT);
    }

    // Add items to cart
    if (available.length > 0) {
      await addToCart(available, { render: false });
      console.info(`[Shared Cart] Added ${available.length} items to cart`);
    } else {
      console.warn('[Shared Cart] No items available to add');
    }
  } catch (error) {
    displayError(UserMessages.PARTIAL_IMPORT);
    console.error('[Shared Cart] Error during product import:', error);
  }

  // Step 4: Apply discount codes
  if (payload.promotionCodes?.length) {
    try {
      // ⚠️ TEMPORARY — Remove after April 30, 2026
      // Birthday promotion: skip "birthday-" discount codes during import
      // to avoid conflicts with multi-GWP sub-promotions.
      const discountCodes = payload.promotionCodes
        .map((x) => x.code)
        .filter((code) => !code.toLowerCase().startsWith('birthday-'));
      const discountCodesStr = discountCodes.join(',');

      if (discountCodesStr) {
        await applyDiscountCode(discountCodesStr, { render: false });
        console.info('[Shared Cart] Applied discount codes:', discountCodes);
      }
    } catch (error) {
      displayError(UserMessages.DISCOUNT_FAILED);
      console.error('[Shared Cart] Error applying discount codes:', error);
    }
  }

  // Step 5: Single authoritative render — reflects the real cart state
  // regardless of which operations above succeeded or failed
  try {
    await refreshCartUI();
  } catch (error) {
    console.error('[Shared Cart] Error refreshing cart UI:', error);
  }

  // Step 6: Cleanup - remove handoff param from URL
  removeUrlParam('handoff');
  console.info('[Shared Cart] Cart import complete');
}
